import type { ReactNode } from 'react'
import { useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'
import { getBillingPortalSummary } from '../../lib/services/billing'

/** Pure helpers exported for unit tests (#1769). */

export function isExpiredTrial(status: string, trialEndsAt: string | null | undefined): boolean {
  if (status !== 'trialing') return false
  if (trialEndsAt == null || String(trialEndsAt).trim() === '') return true // fail closed
  const parsed = new Date(trialEndsAt)
  if (Number.isNaN(parsed.valueOf())) return true // fail closed on malformed
  return parsed.valueOf() <= Date.now()
}

export function isBillingLocked(status: string | null | undefined, trialEndsAt: string | null | undefined): boolean {
  if (status == null || String(status).trim() === '') return true // missing profile → deny
  const s = String(status).toLowerCase()
  if (isExpiredTrial(s, trialEndsAt)) return true
  return s === 'past_due' || s === 'unpaid' || s === 'canceled' || s === 'inactive'
}

/** True when paid/protected children must not render (loading, error, locked). */
export function shouldBlockProtectedChildren(opts: {
  isLoading: boolean
  isError: boolean
  hasData: boolean
  status?: string | null
  trialEndsAt?: string | null
}): boolean {
  if (opts.isLoading) return true
  if (opts.isError) return true
  if (!opts.hasData) return true
  return isBillingLocked(opts.status, opts.trialEndsAt)
}

/** Production/staging default fail-closed. Playwright gate may set false for smoke without full billing seed. */
function isBillingGateFailClosed(): boolean {
  const raw = (import.meta.env.VITE_BILLING_GATE_FAIL_CLOSED as string | undefined) ?? 'true'
  return String(raw).trim().toLowerCase() !== 'false'
}

export function BillingStatusGate({ children }: { children: ReactNode }) {
  const { session, viewerContext } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const failClosed = isBillingGateFailClosed()

  const summaryQuery = useQuery({
    queryKey: ['billing-access-status'],
    queryFn: getBillingPortalSummary,
    enabled: !!session,
    staleTime: 60_000,
  })

  const allowCurrentRoute = location.pathname === '/billing' || location.pathname === '/onboarding'
  const block = shouldBlockProtectedChildren({
    isLoading: summaryQuery.isLoading || summaryQuery.isPending,
    isError: summaryQuery.isError,
    hasData: !!summaryQuery.data,
    status: summaryQuery.data?.subscription_status,
    trialEndsAt: summaryQuery.data?.trial_ends_at,
  })
  const lockoutActive = block && !!summaryQuery.data
    ? isBillingLocked(summaryQuery.data.subscription_status, summaryQuery.data.trial_ends_at)
    : block

  useEffect(() => {
    if (!failClosed || !lockoutActive || allowCurrentRoute) return
    if (viewerContext?.role !== 'Admin') return
    if (!summaryQuery.data) return
    const reason = isExpiredTrial(summaryQuery.data.subscription_status, summaryQuery.data.trial_ends_at)
      ? 'trial-expired'
      : 'billing-required'
    navigate(`/billing?billing=${reason}`, { replace: true })
  }, [allowCurrentRoute, failClosed, lockoutActive, navigate, summaryQuery.data, viewerContext?.role])

  // Explicit e2e opt-out (playwright gate only) — production builds leave default true
  if (!failClosed) {
    return <>{children}</>
  }

  if (allowCurrentRoute) {
    return <>{children}</>
  }

  if (summaryQuery.isLoading || summaryQuery.isPending) {
    return (
      <div className="mx-auto max-w-2xl rounded-xl border border-slate-200 bg-slate-50 p-6 text-slate-700">
        <p className="text-sm">Verifying subscription status…</p>
      </div>
    )
  }

  if (summaryQuery.isError || !summaryQuery.data) {
    return (
      <div className="mx-auto max-w-2xl rounded-xl border border-amber-200 bg-amber-50 p-6 text-amber-900">
        <h1 className="font-brand text-2xl font-bold text-[var(--brand-deep)]">Billing status unavailable</h1>
        <p className="mt-2 text-sm">
          We could not verify your subscription. Protected workspace access is blocked until billing status loads successfully.
        </p>
      </div>
    )
  }

  // Fail-closed for ALL roles when locked (including Admin). Admin is redirected
  // to /billing via useEffect; never render protected children while locked (#1769).
  if (lockoutActive) {
    const isAdmin = viewerContext?.role === 'Admin'
    return (
      <div className="mx-auto max-w-2xl rounded-xl border border-amber-200 bg-amber-50 p-6 text-amber-900">
        <h1 className="font-brand text-2xl font-bold text-[var(--brand-deep)]">Subscription Attention Required</h1>
        <p className="mt-2 text-sm">
          {isAdmin
            ? 'Your tenant subscription needs attention. Redirecting to billing…'
            : 'Your tenant subscription needs administrator attention before normal workspace access can continue.'}
        </p>
        {!isAdmin && (
          <p className="mt-2 text-sm">
            Contact your tenant administrator to complete billing or finish onboarding.
          </p>
        )}
      </div>
    )
  }

  return <>{children}</>
}
